import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, FileText } from 'lucide-react';
import { useProject } from '../../../contexts/ProjectContext';
import {
  contentApi,
  STATUS_LABELS,
  type ContentItem,
  type Author,
} from '../../../api/content';
import { CalendarGrid } from '../../../components/shared/CalendarGrid';
import { ContentCard } from '../../../components/shared/ContentCard';
import {
  Select,
  Card,
  Skeleton,
  EmptyState,
  Chip,
} from '../../../components/ui';
import { DashboardPageLayout } from '../../../components/layout';

const ContentCalendarPage = () => {
  const navigate = useNavigate();
  const { currentProject, buildProjectUrl } = useProject();
  const workspaceId = currentProject?.workspaceId;
  const [items, setItems] = useState<ContentItem[]>([]);
  const [authors, setAuthors] = useState<Author[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [authorFilter, setAuthorFilter] = useState('');
  const [campaignFilter, setCampaignFilter] = useState('');

  const hasActiveFilters = authorFilter !== '' || campaignFilter !== '';

  useEffect(() => {
    if (!workspaceId) return;
    contentApi.listAuthors(workspaceId)
      .then(setAuthors)
      .catch(() => setAuthors([]));
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId) return;
    setLoading(true);
    // Fetch items for visible range (prev month last week to next month first week)
    const from = new Date(currentMonth);
    from.setDate(from.getDate() - 7);
    const to = new Date(currentMonth);
    to.setMonth(to.getMonth() + 1);
    to.setDate(to.getDate() + 7);

    contentApi.getCalendarItems(
      workspaceId,
      from.toISOString().split('T')[0],
      to.toISOString().split('T')[0]
    )
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [workspaceId, currentMonth]);

  // Extract unique campaigns for dropdown
  const campaigns = useMemo(() => {
    const set = new Set<string>();
    for (const item of items) {
      if (item.campaign) set.add(item.campaign);
    }
    return Array.from(set).sort();
  }, [items]);

  // Build Select options
  const authorOptions = useMemo(
    () => [
      { value: '', label: 'All Authors' },
      ...authors.map(a => ({ value: a.id, label: a.name })),
    ],
    [authors]
  );

  const campaignOptions = useMemo(
    () => [
      { value: '', label: 'All Campaigns' },
      ...campaigns.map(c => ({ value: c, label: c })),
    ],
    [campaigns]
  );

  // Apply client-side filters
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      if (authorFilter && item.primary_author_id !== authorFilter) return false;
      if (campaignFilter && item.campaign !== campaignFilter) return false;
      return true;
    });
  }, [items, authorFilter, campaignFilter]);

  // Check if current month view has any items
  const currentMonthHasItems = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    return filteredItems.some(item => {
      if (!item.planned_publish_date) return false;
      const d = new Date(item.planned_publish_date);
      return d.getFullYear() === year && d.getMonth() === month;
    });
  }, [filteredItems, currentMonth]);

  // Resolve the active author name for filter chip display
  const activeAuthorName = useMemo(() => {
    if (!authorFilter) return '';
    const author = authors.find(a => a.id === authorFilter);
    return author?.name ?? authorFilter;
  }, [authorFilter, authors]);

  const clearAllFilters = () => {
    setAuthorFilter('');
    setCampaignFilter('');
  };

  return (
    <DashboardPageLayout
      title="Content Calendar"
      subtitle="Plan and visualize your content schedule"
      icon={<Calendar size={20} />}
    >
      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <Select
          size="small"
          value={authorFilter}
          onChange={e => setAuthorFilter(e.target.value)}
        >
          {authorOptions.map(o => (
            <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>
          ))}
        </Select>

        <Select
          size="small"
          value={campaignFilter}
          onChange={e => setCampaignFilter(e.target.value)}
        >
          {campaignOptions.map(o => (
            <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>
          ))}
        </Select>
      </div>

      {/* Active filter chips */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {authorFilter && (
            <Chip
              type="filter"
              label="Author"
              value={activeAuthorName}
              size="sm"
              onRemove={() => setAuthorFilter('')}
            />
          )}
          {campaignFilter && (
            <Chip
              type="filter"
              label="Campaign"
              value={campaignFilter}
              size="sm"
              onRemove={() => setCampaignFilter('')}
            />
          )}
          <button
            type="button"
            onClick={clearAllFilters}
            className="text-sm text-subtle hover:text-text underline transition-colors"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Calendar Grid */}
      {loading ? (
        <Card padding="none">
          <div className="p-16 flex flex-col items-center justify-center gap-4">
            <Skeleton variant="rounded" width={240} height={20} />
            <Skeleton variant="rounded" width={360} height={16} />
            <div className="grid grid-cols-7 gap-2 w-full max-w-lg mt-4">
              {Array.from({ length: 7 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={80} />
              ))}
            </div>
          </div>
        </Card>
      ) : (
        <>
          <Card padding="none">
            <CalendarGrid<ContentItem>
              currentMonth={currentMonth}
              onMonthChange={setCurrentMonth}
              items={filteredItems}
              getItemDate={item => item.planned_publish_date}
              renderItem={item => (
                <ContentCard
                  compact
                  item={item}
                  onClick={() => navigate(buildProjectUrl(`co/${item.id}`, 'sm'))}
                />
              )}
            />
          </Card>

          {/* Empty month message */}
          {!currentMonthHasItems && (
            <EmptyState
              size="sm"
              icon={<FileText size={32} strokeWidth={1.5} />}
              title={`No content scheduled for ${currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`}
            />
          )}
        </>
      )}
    </DashboardPageLayout>
  );
};

export default ContentCalendarPage;
